import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CorridorPageView } from "@/components/corridor/CorridorPageView";
import { DEFAULT_CORRIDOR_SLUG, getCorridorBySlug } from "@/data/corridors/registry";
import { isReversePublicCorridorSlug, resolveCorridorSlug } from "@/data/corridors/corridorSeoSlugs";
import type { CorridorDefinition } from "@/types/corridor";
import { getHostWaitingRoutes, type HostRouteDto } from "@/services/availableHostsService";
import { fetchRoutePageMock, mergeApiCorridorIntoResolved } from "@/services/routePageMockApi";
import type { HostedRide } from "@/data/sampleRoutes";
import NotFound from "@/pages/NotFound";

/**
 * Intercity marketing page: `/` (default corridor) and `/route/:slug` (legacy `/corridor/:slug` redirects).
 * Page copy + ride cards are driven by JSON mock “API” when available; live API fills gaps.
 */
export default function CorridorPage() {
  const { slug: slugParam } = useParams<{ slug?: string }>();
  const slug = slugParam ?? DEFAULT_CORRIDOR_SLUG;

  const reverseSlug = (value: string) => value.split("-").reverse().join("-");

  const resolvedData = useMemo(() => {
    if (!slug) return undefined;

    const internal = resolveCorridorSlug(slug);
    const base = getCorridorBySlug(internal);
    if (!base) {
      const reversed = getCorridorBySlug(reverseSlug(slug));
      if (!reversed) return undefined;
      return {
        ...reversed,
        slug,
        cityA: reversed.cityB,
        cityB: reversed.cityA,
        stats: {
          ...reversed.stats,
          zonesA: reversed.stats.zonesB,
          zonesB: reversed.stats.zonesA,
        },
      };
    }

    if (isReversePublicCorridorSlug(slug)) {
      return {
        ...base,
        slug,
        cityA: base.cityB,
        cityB: base.cityA,
        stats: {
          ...base.stats,
          zonesA: base.stats.zonesB,
          zonesB: base.stats.zonesA,
        },
      };
    }

    return { ...base, slug };
  }, [slug]);

  const canonicalSlug = useMemo(() => {
    if (!slug) return undefined;
    return getCorridorBySlug(slug)?.slug ?? getCorridorBySlug(reverseSlug(slug))?.slug;
  }, [slug]);

  const [dynamicData, setDynamicData] = useState<CorridorDefinition | undefined>(resolvedData);
  const [apiRides, setApiRides] = useState<{ forward: HostedRide[]; reverse: HostedRide[] }>({
    forward: [],
    reverse: [],
  });

  useEffect(() => {
    setDynamicData(resolvedData);
    setApiRides({ forward: [], reverse: [] });
  }, [resolvedData]);

  useEffect(() => {
    if (!resolvedData || !canonicalSlug) return;

    const canonicalCorridor = getCorridorBySlug(canonicalSlug);
    if (!canonicalCorridor) return;

    let cancelled = false;

    const findMatchingRoute = (routes: HostRouteDto[], fromCity: string, toCity: string) =>
      routes.find((route) => {
        const from = route.from ?? route.fromCity ?? "";
        const to = route.to ?? route.toCity ?? "";
        return from.toLowerCase() === fromCity.toLowerCase() && to.toLowerCase() === toCity.toLowerCase();
      });

    const mapRouteCardsToHostedRides = (
      route: HostRouteDto | undefined,
      fromCity: string,
      toCity: string
    ): HostedRide[] =>
      (route?.cards ?? []).map((card, index) => {
        const dateFromDateTime = card.rideDateTime?.split("T")[0] ?? "";
        const timeFromDateTime = card.rideDateTime?.split("T")[1]?.slice(0, 5) ?? "";
        return {
          id: `${fromCity}-${toCity}-${index}-${card.rideDateTime ?? card.date ?? "ride"}`,
          from: fromCity,
          to: toCity,
          date: card.date || dateFromDateTime || "N/A",
          time: card.time || timeFromDateTime || "--:--",
          seatsAvailable: card.seatsAvailable ?? 0,
          pricePerSeat: typeof card.price === "number" ? Math.round(card.price) : 0,
          vehicleType: card.vehicle?.trim() || "Car",
          rating: typeof card.rating === "number" && card.rating > 0 ? card.rating : 4.5,
        };
      });

    const run = async () => {
      const mock = await fetchRoutePageMock(canonicalSlug);
      if (cancelled) return;

      if (mock?.ok) {
        setDynamicData(mergeApiCorridorIntoResolved(resolvedData, mock.corridor, canonicalCorridor));
        setApiRides({
          forward: mock.rides.forward ?? [],
          reverse: mock.rides.reverse ?? [],
        });
        return;
      }

      setDynamicData(resolvedData);
      setApiRides({ forward: [], reverse: [] });

      try {
        const [forward, reverse] = await Promise.all([
          getHostWaitingRoutes({
            fromCity: resolvedData.cityA,
            toCity: resolvedData.cityB,
            page: 1,
          }),
          getHostWaitingRoutes({
            fromCity: resolvedData.cityB,
            toCity: resolvedData.cityA,
            page: 1,
          }),
        ]);

        if (cancelled) return;

        const forwardRoute = findMatchingRoute(forward.routes ?? [], resolvedData.cityA, resolvedData.cityB);
        const reverseRoute = findMatchingRoute(reverse.routes ?? [], resolvedData.cityB, resolvedData.cityA);

        const forwardCount =
          forwardRoute?.routeCount ?? forwardRoute?.count ?? forwardRoute?.cards?.length ?? 0;
        const reverseCount =
          reverseRoute?.routeCount ?? reverseRoute?.count ?? reverseRoute?.cards?.length ?? 0;
        const forwardRides = mapRouteCardsToHostedRides(
          forwardRoute,
          resolvedData.cityA,
          resolvedData.cityB
        );
        const reverseRides = mapRouteCardsToHostedRides(
          reverseRoute,
          resolvedData.cityB,
          resolvedData.cityA
        );

        const priceCandidates = [...(forwardRoute?.cards ?? []), ...(reverseRoute?.cards ?? [])]
          .map((card) => card.price)
          .filter((value): value is number => typeof value === "number" && value > 0);

        setDynamicData((current) => {
          if (!current) return current;
          return {
            ...current,
            stats: {
              ...current.stats,
              ridesToday: forwardCount + reverseCount || current.stats.ridesToday,
              carpoolFare:
                priceCandidates.length > 0
                  ? Math.round(Math.min(...priceCandidates))
                  : current.stats.carpoolFare,
            },
          };
        });
        setApiRides({
          forward: forwardRides,
          reverse: reverseRides,
        });
      } catch {
        /* keep empty rides + static corridor */
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [resolvedData, canonicalSlug]);

  if (!dynamicData) return <NotFound />;
  return <CorridorPageView corridor={dynamicData} apiLiveRides={apiRides} />;
}
